import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';

function RepairUserProfile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [status, setStatus] = useState('checking');
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    agreeTerms: false
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkUser = async () => {
      const user = auth.currentUser;
      if (user) {
        setCurrentUser(user);
        
        // Check if user profile exists in Firestore
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);
        
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            // User profile exists
            setStatus('profile_exists');
            setMessage('Your user profile already exists in the database.');
            
            // Pre-fill form with existing data
            const userData = userDoc.data();
            setUserInfo({
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              email: userData.email || user.email || '',
              country: userData.country || '',
              agreeTerms: userData.licenseAgreed || false
            });
          } else {
            // No user profile
            setStatus('missing_profile');
            setMessage('Your user profile is missing. You can create it below.');
            
            // Pre-fill email from authentication
            setUserInfo(prev => ({
              ...prev,
              email: user.email || ''
            }));
          }
        } catch (error) {
          console.error("Error checking user profile:", error);
          setStatus('error');
          setMessage(`Error checking profile: ${error.message}`);
        }
      } else {
        setStatus('not_logged_in');
        setMessage('You are not currently logged in.');
      }
    };
    
    checkUser();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      setStatus('saving');
      setMessage('Creating your profile...');
      
      const db = getFirestore();
      const userRef = doc(db, "users", currentUser.uid);
      
      await setDoc(userRef, {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        country: userInfo.country,
        licenseAgreed: userInfo.agreeTerms,
        licenseAgreedDate: userInfo.agreeTerms ? Timestamp.now() : null,
        registrationDate: Timestamp.now(),
        yearlyBoardCount: 0,
        lastResetDate: Timestamp.now()
      });
      
      setStatus('success');
      setMessage('Your profile has been created successfully. You can now access all features.');
    } catch (error) {
      console.error("Error creating user profile:", error);
      setStatus('error');
      setMessage(`Error creating profile: ${error.message}`);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="bg-white p-5 rounded-lg shadow-md">
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="firstName">
          First Name
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={userInfo.firstName}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="lastName">
          Last Name
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={userInfo.lastName}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="email">
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={userInfo.email}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md"
          required
          readOnly={currentUser?.email}
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="country">
          Country
        </label>
        <input
          type="text"
          id="country"
          name="country"
          value={userInfo.country}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>
      
      <div className="mb-6">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="agreeTerms"
            name="agreeTerms"
            checked={userInfo.agreeTerms}
            onChange={handleChange}
            className="mt-1 mr-2"
            required
          />
          <label htmlFor="agreeTerms" className="text-sm">
            I have read and agree to the Waka Folding Technology Non-Commercial License.
          </label>
        </div>
      </div>
      
      <button
        type="submit"
        disabled={status === 'saving'}
        className={`w-full py-2 px-4 rounded-md text-white ${
          status === 'saving' ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {status === 'saving' ? 'Creating Profile...' : 'Create Profile'}
      </button>
    </form>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-md">
      <h1 className="text-2xl font-bold mb-4">User Profile Repair</h1>
      
      {message && (
        <div className={`p-4 mb-4 rounded-md ${
          status === 'error' ? 'bg-red-100 text-red-700' :
          status === 'success' ? 'bg-green-100 text-green-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {message}
        </div>
      )}
      
      {currentUser ? (
        <div>
          <div className="mb-4 p-4 bg-gray-100 rounded-md">
            <p><strong>User ID:</strong> {currentUser.uid}</p>
            <p><strong>Email:</strong> {currentUser.email}</p>
          </div>
          
          {(status === 'missing_profile') && renderForm()}
          
          {status === 'success' && (
            <div className="mt-4 text-center">
              <button
                onClick={() => window.location.href = '/documents'}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Go to Documents
              </button>
            </div>
          )}
        </div>
      ) : status === 'checking' ? (
        <p>Checking user status...</p>
      ) : (
        <div>
          <p>You need to be logged in to repair your profile.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      )}
    </div>
  );
}

export default RepairUserProfile;